import { IndexedFilters, FilterRange } from '@/catalog/types/filters';
import { FilterOptionTextInput } from './filter-option-text-input';
import { Slider } from '@/shadcn-ui/slider';
import { Dispatch, SetStateAction, useCallback } from 'react';
import { useFilterSlider } from '@/catalog/hooks/use-filter-slider';
import AlertSmall from '@/icons/alert-small';
import { cn } from '@/shadcn-ui/utils';
import { abbreviateNumberLabel } from '@/catalog/utils/abbreviate-number-label';
import { isTypeNumberArray } from '@/catalog/utils/filter-menu-type-guards';

export type SliderFilters = number[] | FilterRange<number>;

interface FilterOptionSliderProps {
  categoryName: string;
  filters: SliderFilters;
  selectedFilters: SliderFilters;
  setSelectedFilters: Dispatch<SetStateAction<IndexedFilters>>;
  prefixVariant: 'none' | 'dollar';
  step?: number;
  footerNote?: string | React.ReactNode;
  allowThumbOverlap?: boolean;
  abbreviateThumbLabels?: boolean;
  disableThumbLabels?: boolean;
  showEdgeLabels?: boolean;
}

// When the enter key is pressed, update the selected min/max based on what is currently in the text input.
function handleKeyUp(e: React.KeyboardEvent<HTMLInputElement>) {
  if (e.key === 'Enter') {
    e.currentTarget.blur();
  }
}

export default function FilterOptionSlider({
  categoryName,
  filters,
  selectedFilters,
  setSelectedFilters,
  prefixVariant = 'none',
  step = 1,
  footerNote,
  allowThumbOverlap = false,
  abbreviateThumbLabels = false,
  disableThumbLabels = false,
  showEdgeLabels = false,
}: FilterOptionSliderProps) {
  const prefix = prefixVariant === 'dollar' ? '$' : '';

  const {
    sliderMin,
    sliderMax,
    minValue,
    maxValue,
    leftThumbValue,
    rightThumbValue,
    value,
    textInputMinError,
    textInputMaxError,
    handleMinChange,
    handleMaxChange,
    handleMinBlur,
    handleMaxBlur,
    handleSliderChange,
    handleSliderCommitDebounced,
  } = useFilterSlider({
    categoryName,
    filters,
    prefix,
    allowThumbOverlap,
    step,
    selectedFilters,
    setSelectedFilters,
  });

  const constructLabel = useCallback(
    (value: string) => {
      if (abbreviateThumbLabels) {
        return prefix
          ? `${prefix}${abbreviateNumberLabel(Number(value.replaceAll(prefix, '')))}`
          : abbreviateNumberLabel(Number(value));
      } else {
        return value;
      }
    },
    [abbreviateThumbLabels, prefix]
  );

  const constructEdgeLabel = useCallback(
    (value: number) => {
      if (isTypeNumberArray(filters)) {
        return prefix + constructLabel(filters[value].toString());
      } else {
        return prefix + constructLabel(value.toString());
      }
    },
    [constructLabel, filters, prefix]
  );

  return (
    <div className="flex flex-col gap-[24px]">
      <div className="flex flex-col gap-[8px]">
        <div className="flex items-center gap-[16px]">
          <FilterOptionTextInput
            name={`${categoryName}-min`}
            value={minValue}
            onBlur={handleMinBlur}
            onChange={handleMinChange}
            onKeyUp={handleKeyUp}
            className={cn({
              'border-flexcar-red50 focus:border-flexcar-red50': textInputMinError,
            })}
          />
          <p className="body text-center">-</p>
          <FilterOptionTextInput
            name={`${categoryName}-max`}
            value={maxValue}
            onBlur={handleMaxBlur}
            onChange={handleMaxChange}
            onKeyUp={handleKeyUp}
            className={cn({
              'border-flexcar-red50 focus:border-flexcar-red50': textInputMaxError,
            })}
          />
        </div>
        {textInputMinError || textInputMaxError ? (
          <div className="flex items-center gap-[4px]">
            <AlertSmall className="flex-shrink-0" />
            <p className="body3 text-flexcar-red50">Please enter a valid number</p>
          </div>
        ) : null}
      </div>

      <Slider
        {...(disableThumbLabels
          ? {}
          : {
              popoverConfiguration: [
                {
                  displayValue: constructLabel(leftThumbValue),
                  insideThumb: true,
                },
                {
                  displayValue: constructLabel(rightThumbValue),
                  insideThumb: true,
                },
              ],
            })}
        {...(showEdgeLabels
          ? {
              edgeLabelsConfiguration: {
                minDisplayValue: constructEdgeLabel(sliderMin),
                maxDisplayValue: constructEdgeLabel(sliderMax),
              },
            }
          : {})}
        value={value}
        onValueChange={handleSliderChange}
        onValueCommit={handleSliderCommitDebounced}
        min={sliderMin}
        max={sliderMax}
        step={step}
        minStepsBetweenThumbs={allowThumbOverlap ? 0 : 1}
        rootClassName="mb-[28px]"
      />

      {footerNote ? (
        <p className="body3 text-flexcar-neutral20">
          <span className="align-text-top">*</span>
          {footerNote}
        </p>
      ) : null}
    </div>
  );
}
